import { z } from 'zod'

// ===== Status Enum =====
export const ExpensesStatusSchema = z.enum(['pending', 'approved', 'updating', 'cancelled'])

// ===== Approval Metadata Schema =====
export const ApprovalMetadataSchema = z.object({
  userId: z.string(),
  approvalDate: z.string().optional(),
  rejectionDate: z.string().optional(),
  notes: z.string().optional(),
})

// ===== Category Schemas =====
export const ExpenseCategorySchema = z.object({
  _id: z.string(),
  name: z.string().min(1, 'Category name is required'),
  description: z.string().optional(),
  active: z.boolean(),
  createdAt: z.string().optional(),
  createdBy: z.string().optional(),
  updatedAt: z.string().optional(),
  updatedBy: z.string().optional(),
})

export const ExpenseCategoryCreateSchema = z.object({
  name: z.string().min(1, 'Category name is required'),
  description: z.string().optional(),
})

export const ExpenseCategoryUpdateSchema = ExpenseCategoryCreateSchema.extend({
  _id: z.string().min(1, 'Category ID is required'),
  active: z.boolean(),
})

// ===== Expense Item Schemas =====
export const ExpenseItemDataSchema = z.object({
  _id: z.string(),
  categoryId: z.string().min(1, 'Category ID is required'),
  itemDescription: z.string().min(1, 'Item description is required'),
  amount: z.number().min(0, 'Amount must be positive'),
  receiptUrl: z.string(),
})

export const ExpenseItemInputSchema = z.object({
  categoryId: z.string().min(1, 'Category ID is required'),
  itemDescription: z.string().min(1, 'Item description is required'),
  amount: z.number().min(0.01, 'Amount must be greater than 0'),
  file: z.instanceof(File),
})

export const ExpenseItemSchema = z.object({
  _id: z.string(),
  refNumber: z.string(),
  churchId: z.string().min(1, 'Church ID is required'),
  requester: z.string().min(1, 'Requester Name is required'),
  requesterEmail: z.string().email('Valid email is required'),
  expenseItems: z
    .array(ExpenseItemDataSchema)
    .min(1, 'At least one expense item is required'),
  date: z.string().min(1, 'Expense date is required'),
  notes: z.string().optional(),
  amount: z.number().min(0, 'Amount must be positive'),
  status: ExpensesStatusSchema,
  createdAt: z.string(),
  createdBy: z.string(),
  localPastor: ApprovalMetadataSchema.optional(),
  adminPastor: ApprovalMetadataSchema.optional(),
  adminTreasurer: ApprovalMetadataSchema.optional(),
  updatedAt: z.string().optional(),
  updatedBy: z.string().optional(),
})

export const ExpenseCreateSchema = z.object({
  churchId: z.string().min(1, 'Church ID is required'),
  requester: z.string().min(1, 'Requester Name is required'),
  requesterEmail: z.string().email('Valid email is required'),
  expenseItems: z
    .array(ExpenseItemInputSchema)
    .min(1, 'At least one expense item is required'),
  amount: z.number().min(0, 'Amount must be positive'),
  date: z.string().min(1, 'Expense date is required'),
  notes: z.string(),
})

export const ExpenseUpdateSchema = z.object({
  _id: z.string().min(1, 'Expense ID is required'),
  churchId: z.string().min(1, 'Church ID is required'),
  requester: z.string().min(1, 'Requester Name is required'),
  requesterEmail: z.string().email('Valid email is required'),
  expenseItems: z
    .array(ExpenseItemInputSchema.or(ExpenseItemDataSchema))
    .min(1, 'At least one expense item is required'),
  amount: z.number().min(0, 'Amount must be positive'),
  date: z.string().min(1, 'Expense date is required'),
  notes: z.string(),
})

export const ExpenseApprovalSchema = z.object({
  _id: z.string().min(1, 'Expense ID is required'),
  action: z.enum(['approve', 'reject']),
  notes: z.string().optional(),
  reimbursementDate: z.string().optional(),
  rejectionType: z.enum(['cancel', 'update']).optional(),
})

// ===== Type Exports =====
export type ExpensesStatus = z.infer<typeof ExpensesStatusSchema>
export type ApprovalMetadata = z.infer<typeof ApprovalMetadataSchema>

export type ExpenseCategoryData = z.infer<typeof ExpenseCategorySchema>
export type ExpenseCategoryCreateData = z.infer<
  typeof ExpenseCategoryCreateSchema
>
export type ExpenseCategoryUpdateData = z.infer<
  typeof ExpenseCategoryUpdateSchema
>

export type ExpenseItemData = z.infer<typeof ExpenseItemDataSchema>
export type ExpenseItemInput = z.infer<typeof ExpenseItemInputSchema>
export type ExpenseData = z.infer<typeof ExpenseItemSchema>
export type ExpenseCreateData = z.infer<typeof ExpenseCreateSchema>
export type ExpenseUpdateData = z.infer<typeof ExpenseUpdateSchema>
export type ExpenseApprovalData = z.infer<typeof ExpenseApprovalSchema>
